import { useState, useEffect, useRef } from "react";
import { useAuthStore } from "@repo/api-client";
import { trpc } from "../lib/trpc";
import { QRCodeSVG } from "qrcode.react";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";

const normalizeText = (text: string | null | undefined): string => {
  if (!text) return "";
  return text
    .normalize("NFC")
    .toLowerCase()
    .replace(/\u0E40\u0E40/g, "\u0E41") // normalize twin sara e to sara ae
    .trim();
};

export default function GenerateQR() {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/");
  };
  const [selectedClass, setSelectedClass] = useState<string>("LG");
  const [selectedMemberId, setSelectedMemberId] = useState<number | undefined>(
    undefined,
  );
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [token, setToken] = useState<string>("");
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const qrRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Click outside to close member selection dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // If user is not authenticated or lacks structural IDs, redirect
  useEffect(() => {
    if (!user || !user.StructID || !user.ChurchID) {
      navigate("/profile");
    }
  }, [user, navigate]);

  // Fetch structure classes
  const { data: structureClasses } = trpc.structureClass.getAll.useQuery(
    undefined,
    {
      enabled: !!user,
    },
  );

  // Query member tree under user's Structure ID
  const {
    data: memberTree,
    isLoading: isMembersLoading,
    isFetching: isMembersFetching,
  } = trpc.structureMember.getMemberTree.useQuery(
    {
      parentNodeId: user?.StructID ?? 0,
      structureClassCode: selectedClass,
    },
    {
      enabled: !!user?.StructID && !!selectedClass,
    },
  );

  const showLoading = isMembersLoading || isMembersFetching;

  // Flatten members from the structure tree response
  const membersList = (memberTree || []).flatMap((group) =>
    (group.members || []).map((m) => ({
      ...m,
      structureName: group.structure.StrucName,
    })),
  );

  const filteredMembers = membersList.filter((m) => {
    const selectedMember = membersList.find(
      (x) => x.MemID === selectedMemberId,
    );
    const selectedName = selectedMember
      ? `${selectedMember.Name} ${selectedMember.Surname}${selectedMember.Nickname ? ` (${selectedMember.Nickname})` : ""}`
      : "";

    if (
      selectedMember &&
      normalizeText(searchTerm) === normalizeText(selectedName)
    ) {
      return true;
    }

    const term = normalizeText(searchTerm);
    if (term === "**") {
      return true;
    }
    if (term.length < 2) {
      return false;
    }

    const fullName = normalizeText(`${m.Name} ${m.Surname}`);
    const nickname = normalizeText(m.Nickname);
    const structure = normalizeText(m.structureName);
    return (
      fullName.includes(term) ||
      nickname.includes(term) ||
      structure.includes(term)
    );
  });

  // Generate token query/mutation call
  const { refetch: fetchNewToken, isFetching: isGeneratingToken } =
    trpc.auth.generateFastSignupToken.useQuery(
      {
        churchId: user?.ChurchID ?? 0,
        mappedMemId: selectedMemberId,
      },
      {
        enabled: false, // Trigger manually
      },
    );

  const handleGenerate = async () => {
    const res = await fetchNewToken();
    if (res.data?.token) {
      setToken(res.data.token);
      setTimeLeft(900); // 15 minutes in seconds
    }
  };

  // Trigger token generation when member is selected
  useEffect(() => {
    if (selectedMemberId) {
      handleGenerate();
    } else {
      setToken("");
      setTimeLeft(0);
    }
  }, [selectedMemberId]);

  // Countdown timer for 15 minutes token validity
  useEffect(() => {
    if (timeLeft <= 0) return;
    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setToken(""); // clear token on expiry
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [timeLeft]);

  const [copied, setCopied] = useState(false);
  const signupLink = token
    ? `${window.location.origin}/fast-signup/signup?token=${token}`
    : "";

  const handleCopyLink = () => {
    if (!signupLink) return;
    navigator.clipboard.writeText(signupLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadQR = () => {
    const svg = qrRef.current?.querySelector("svg");
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const svgSize = svg.getBoundingClientRect();
    canvas.width = svgSize.width || 250;
    canvas.height = svgSize.height || 250;
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.onload = () => {
      if (ctx) {
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        const a = document.createElement("a");
        a.download = `fast_signup_qr_${selectedMemberId || "code"}.png`;
        a.href = canvas.toDataURL("image/png");
        a.click();
      }
    };
    img.src =
      "data:image/svg+xml;base64," +
      btoa(unescape(encodeURIComponent(svgData)));
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 p-4 md:p-8 flex flex-col font-sans">
      <div className="max-w-4xl w-full mx-auto bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden border border-slate-200/60 transition-all duration-300">
        {/* Header Banner */}
        <div className="relative bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-6 py-8 md:px-12 flex flex-col md:flex-row justify-between items-center text-white gap-4">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.15),transparent_45%)] pointer-events-none"></div>
          <div className="flex items-center gap-4 z-10">
            <button
              onClick={() => navigate(-1)}
              className="p-2.5 bg-white/10 hover:bg-white/20 active:scale-95 text-white rounded-xl transition-all border border-white/10 backdrop-blur-sm"
            >
              <ArrowBackIcon />
            </button>
            <div>
              <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight flex flex-col">
                <span>{t("generateQr.title")}</span>
                <span className="text-sm md:text-base font-semibold text-indigo-200 mt-1">
                  {t("generateQr.titleSuffix")}
                </span>
              </h1>
              <p className="text-indigo-100 text-xs md:text-sm font-medium opacity-90 mt-1">
                {t("generateQr.subtitle")}
              </p>
              {user && (
                <div className="flex items-center gap-2 mt-2.5">
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 pl-2 pr-3 py-1 rounded-2xl">
                    <div className="h-6 w-6 rounded-full bg-white/20 flex items-center justify-center text-[10px] font-bold text-white overflow-hidden shrink-0 border border-white/10">
                      {user.photoURL ? (
                        <img src={user.photoURL} alt="Profile" className="h-full w-full object-cover" />
                      ) : (
                        (user.first_name?.[0] || user.email?.[0] || "").toUpperCase()
                      )}
                    </div>
                    <div className="text-left leading-none">
                      <div className="text-xs font-bold text-white">
                        {user.first_name} {user.last_name}
                      </div>
                      <span className="text-[9px] text-indigo-100/90 block mt-0.5">
                        {user.email}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="px-3 py-1.5 bg-rose-500/20 hover:bg-rose-500/35 active:scale-95 text-rose-100 hover:text-white rounded-2xl text-xs font-bold border border-rose-500/30 transition-all cursor-pointer leading-none"
                  >
                    {t("common.logout") || "Logout"}
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 z-10">
            <LanguageSwitcher />
            <div className="bg-white/10 backdrop-blur-md border border-white/20 px-4 py-2 rounded-2xl text-sm font-semibold">
              {t("generateQr.churchId")}: {user?.ChurchID}
            </div>
          </div>
        </div>

        {/* Form & QR Grid */}
        <div className="p-6 md:p-10 grid grid-cols-1 lg:grid-cols-12 gap-8 bg-slate-50/30">
          {/* Controls Side */}
          <div className="space-y-6 lg:col-span-7">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <QrCode2Icon className="text-indigo-600" />
                {t("generateQr.selectMemberMapping")}
              </h3>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                  {t("generateQr.structureClassLevel")}
                </label>
                <select
                  value={selectedClass}
                  onChange={(e) => {
                    setSelectedClass(e.target.value);
                    setSelectedMemberId(undefined);
                    setSearchTerm("");
                  }}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all text-slate-700"
                >
                  {structureClasses?.map((cls) => (
                    <option key={cls.StrucClassID} value={cls.code || ""}>
                      {cls.StrucName} ({cls.code})
                    </option>
                  ))}
                  {!structureClasses && (
                    <option value="LG">Life Group (LG)</option>
                  )}
                </select>
              </div>

              <div className="relative" ref={dropdownRef}>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                  {t("generateQr.chooseMember")}
                </label>
                <div className="relative flex items-center">
                  <input
                    type="text"
                    placeholder={t("generateQr.searchPlaceholder")}
                    value={searchTerm}
                    onFocus={() => setIsOpen(true)}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setIsOpen(true);
                      if (selectedMemberId) {
                        setSelectedMemberId(undefined);
                      }
                    }}
                    className="w-full rounded-xl border border-slate-200 pl-4 pr-10 py-2.5 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all text-slate-700 placeholder:text-slate-400"
                  />
                  {showLoading ? (
                    <div className="absolute right-3 flex items-center pointer-events-none">
                      <RefreshIcon
                        className="animate-spin text-slate-400"
                        style={{ fontSize: 18 }}
                      />
                    </div>
                  ) : searchTerm ? (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchTerm("");
                        setSelectedMemberId(undefined);
                      }}
                      className="absolute right-3 text-slate-400 hover:text-slate-600 focus:outline-none text-sm p-1 font-bold"
                    >
                      ✕
                    </button>
                  ) : null}
                </div>

                {isOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                    {showLoading ? (
                      <div className="px-4 py-3 text-sm text-indigo-600 animate-pulse flex items-center gap-2">
                        <RefreshIcon
                          className="animate-spin text-indigo-600"
                          style={{ fontSize: 16 }}
                        />
                        {t("generateQr.loadingMembers")}
                      </div>
                    ) : filteredMembers.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-slate-500">
                        {normalizeText(searchTerm).length < 2
                          ? t("generateQr.typeAtLeastTwo")
                          : t("generateQr.noMembersFound")}
                      </div>
                    ) : (
                      filteredMembers.map((m) => {
                        const isSelected = selectedMemberId === m.MemID;
                        return (
                          <button
                            key={m.MemID}
                            type="button"
                            onClick={() => {
                              setSelectedMemberId(m.MemID);
                              setSearchTerm(
                                `${m.Name} ${m.Surname}${m.Nickname ? ` (${m.Nickname})` : ""}`,
                              );
                              setIsOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex flex-col hover:bg-slate-50 border-b border-slate-100 last:border-b-0 ${
                              isSelected
                                ? "bg-indigo-50/50 hover:bg-indigo-50"
                                : ""
                            }`}
                          >
                            <div className="flex justify-between items-baseline gap-2">
                              <span className="font-semibold text-slate-700">
                                {m.Name} {m.Surname}{" "}
                                {m.Nickname ? `(${m.Nickname})` : ""}
                              </span>
                              <span className="text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full font-medium shrink-0">
                                {m.structureName}
                              </span>
                            </div>
                            {m.Email && (
                              <span className="text-xs text-slate-400 mt-0.5">
                                {m.Email}
                              </span>
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
                {showLoading && !isOpen && (
                  <p className="text-xs text-indigo-600 mt-1 animate-pulse">
                    {t("generateQr.loadingMembers")}
                  </p>
                )}
              </div>
            </div>

            {/* Selected Member Info */}
            {selectedMemberId && (
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-100 rounded-2xl p-6 shadow-sm space-y-3">
                <h4 className="font-bold text-slate-800 text-sm">
                  {t("generateQr.selectedMemberProfile")}
                </h4>
                {(() => {
                  const m = membersList.find(
                    (x) => x.MemID === selectedMemberId,
                  );
                  if (!m) return null;
                  return (
                    <div className="text-sm text-slate-600 space-y-1">
                      <p>
                        <span className="font-semibold">
                          {t("generateQr.fullName")}:
                        </span>{" "}
                        {m.Name} {m.Surname}
                      </p>
                      {m.Nickname && (
                        <p>
                          <span className="font-semibold">
                            {t("generateQr.nickname")}:
                          </span>{" "}
                          {m.Nickname}
                        </p>
                      )}
                      {m.Email && (
                        <p>
                          <span className="font-semibold">
                            {t("generateQr.email")}:
                          </span>{" "}
                          {m.Email}
                        </p>
                      )}
                      {m.Mobile && (
                        <p>
                          <span className="font-semibold">
                            {t("generateQr.phone")}:
                          </span>{" "}
                          {m.Mobile}
                        </p>
                      )}
                      <p>
                        <span className="font-semibold">
                          {t("generateQr.structure")}:
                        </span>{" "}
                        {m.structureName}
                      </p>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          {/* QR Code Output Side */}
          <div className="flex flex-col items-center justify-center bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm lg:col-span-5">
            {token ? (
              <div className="w-full flex flex-col items-center text-center space-y-6">
                <div
                  ref={qrRef}
                  className="p-4 bg-white rounded-2xl shadow-md border border-slate-100 flex items-center justify-center"
                >
                  <QRCodeSVG
                    value={signupLink}
                    size={220}
                    level="H"
                    includeMargin
                  />
                </div>

                {/* Countdown Timer */}
                <div className="space-y-1">
                  <div className="text-sm font-semibold text-slate-500 uppercase tracking-wider">
                    {t("generateQr.expiresIn")}
                  </div>
                  <div
                    className={`text-3xl font-extrabold tabular-nums tracking-tight ${timeLeft < 60 ? "text-rose-500 animate-pulse" : "text-slate-800"}`}
                  >
                    {formatTime(timeLeft)}
                  </div>
                </div>

                {/* Input with Signup URL */}
                <div className="w-full flex items-center bg-slate-50 border border-slate-200 rounded-xl p-2 pl-4">
                  <input
                    type="text"
                    readOnly
                    value={signupLink}
                    className="bg-transparent border-none outline-none text-xs text-slate-600 w-full font-mono select-all"
                  />
                  <button
                    onClick={handleCopyLink}
                    className="ml-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-500 active:scale-95 text-white rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 shadow-sm"
                  >
                    <ContentCopyIcon style={{ fontSize: 14 }} />
                    {copied ? t("generateQr.copied") : t("generateQr.copy")}
                  </button>
                </div>

                <div className="w-full flex gap-3">
                  <button
                    onClick={handleDownloadQR}
                    className="flex-1 py-3 bg-slate-800 hover:bg-slate-700 active:scale-95 text-white rounded-xl font-bold transition-all text-sm flex items-center justify-center gap-2 shadow-md"
                  >
                    <DownloadIcon style={{ fontSize: 18 }} />
                    {t("generateQr.downloadPng")}
                  </button>
                  <button
                    onClick={handleGenerate}
                    disabled={isGeneratingToken}
                    className="px-4 py-3 bg-slate-100 hover:bg-slate-200 active:scale-95 text-slate-700 rounded-xl font-bold transition-all text-sm flex items-center justify-center gap-2 border border-slate-200 disabled:opacity-50"
                  >
                    <RefreshIcon
                      style={{ fontSize: 18 }}
                      className={isGeneratingToken ? "animate-spin" : ""}
                    />
                    {t("generateQr.refresh")}
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 px-6 max-w-sm">
                <div className="h-16 w-16 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center text-slate-400 border border-slate-200">
                  <QrCode2Icon className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-bold text-slate-700 mb-1">
                  {t("generateQr.noQrGenerated")}
                </h3>
                <p className="text-sm text-slate-500">
                  {t("generateQr.selectMemberInstruction")}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
